import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'

// NOTE: these labels are endonyms — the name of each language in that language.
// They must NOT be localized; they read the same in every locale.
const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇲🇽' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
]

const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map(lang => lang.code)

function getDetectedLanguage() {
  if (typeof window === 'undefined') return 'en'

  return window.localStorage.getItem('i18nextLng') ?? window.navigator.language
}

function normalizeLanguage(language?: string) {
  const languageCode = language?.split('-')[0]

  return SUPPORTED_LANGUAGE_CODES.includes(languageCode ?? '') ? languageCode : 'en'
}

interface LanguageSwitcherProps {
  dark?: boolean
  tone?: 'primary'
}

export function LanguageSwitcher({ dark = false, tone }: LanguageSwitcherProps) {
  const { i18n, t: tc } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language ?? getDetectedLanguage())
  const current = LANGUAGES.find(l => l.code === activeLanguage) ?? LANGUAGES[0]

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code)
    setIsOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const btnClass = dark
    ? 'flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 hover:text-white transition-all'
    : tone === 'primary'
    ? 'flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-primary-500 hover:bg-primary-50 hover:text-primary-500 transition-all'
    : 'flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={btnClass}
        aria-label={tc('selectLanguage')}
        title={tc('selectLanguage')}
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">{current.flag} {current.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                activeLanguage === lang.code
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
